import { Suspense, useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { Provider, useDispatch } from 'react-redux';
import { store } from './app/store';
import { router } from './app/routes';
import { setLanguage } from './features/i18n/i18nSlice';
import i18n from './i18n';
import { initializeAnalytics, trackPageView } from './lib/analytics';
import './styles/globals.css';

function LoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        {/* <p className="text-muted-foreground">Loading...</p> */}
      </div>
    </div>
  );
}

function AppInitializer() {
  const dispatch = useDispatch();

  useEffect(() => {
    // Sync language on startup: source of truth is localStorage (or 'ar' fallback)
    const savedLanguage = (localStorage.getItem('language') as 'ar' | 'en' | 'ku') || 'ar';
    const reduxLang = store.getState().i18n.language;

    if (savedLanguage !== reduxLang) {
      dispatch(setLanguage(savedLanguage));
    } else if (i18n.language !== savedLanguage) {
      // Ensure i18n also reflects the saved language
      i18n.changeLanguage(savedLanguage);
    }
  }, [dispatch]);

  return null;
}

function AnalyticsInitializer() {
  useEffect(() => {
    initializeAnalytics();
    trackPageView(`${window.location.pathname}${window.location.search}${window.location.hash}`);
  }, []);

  return null;
}

function AppContent() {
  return (
    <>
      <AppInitializer />
      <AnalyticsInitializer />
      <Suspense fallback={<LoadingFallback />}>
        <RouterProvider router={router} />
      </Suspense>
    </>
  );
}

function App() {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
}

export default App;
